import { useState, useEffect } from "react";
import { Plus, Truck, Pencil, Trash2, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Suppliers({ user }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    vat_number: "",
    notes: ""
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await authFetch(`${API}/suppliers`, { });
      if (response.ok) {
        setSuppliers(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingSupplier 
        ? `${API}/suppliers/${editingSupplier.supplier_id}`
        : `${API}/suppliers`;
      
      const response = await authFetch(url, {
        method: editingSupplier ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingSupplier ? "Fornitore aggiornato" : "Fornitore creato");
        setShowDialog(false);
        resetForm();
        fetchSuppliers();
      } else {
        throw new Error("Errore");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo fornitore?")) return;
    
    try {
      const response = await authFetch(`${API}/suppliers/${supplierId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Fornitore eliminato");
        fetchSuppliers();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDialog = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address,
      vat_number: supplier.vat_number,
      notes: supplier.notes || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      vat_number: "",
      notes: ""
    });
    setEditingSupplier(null);
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.vat_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="suppliers-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Anagrafica Fornitori</h1>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="new-supplier-btn" className="btn-primary">
              <Plus className="w-5 h-5 mr-2" />
              Nuovo Fornitore
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] text-xl">
                {editingSupplier ? "Modifica Fornitore" : "Nuovo Fornitore"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group col-span-2">
                  <label className="form-label">Nome / Ragione Sociale *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefono *</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Indirizzo *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Partita IVA *</label>
                  <input
                    type="text"
                    className="form-input uppercase"
                    value={formData.vat_number}
                    onChange={(e) => setFormData({...formData, vat_number: e.target.value.toUpperCase()})}
                    required
                  />
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Note</label>
                  <textarea
                    className="form-textarea h-20"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowDialog(false)} className="text-zinc-400">
                  Annulla
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingSupplier ? "Salva Modifiche" : "Crea Fornitore"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <input
          type="text"
          placeholder="Cerca fornitore..."
          className="form-input pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Suppliers List */}
      {filteredSuppliers.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Telefono</th>
                <th>P.IVA</th>
                <th className="text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.supplier_id}>
                  <td className="font-medium text-white">{supplier.name}</td>
                  <td>{supplier.email}</td>
                  <td className="font-['JetBrains_Mono']">{supplier.phone}</td>
                  <td className="font-['JetBrains_Mono'] uppercase">{supplier.vat_number}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(supplier)}
                        className="text-primary hover:text-primary/80"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(supplier.supplier_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Truck className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessun fornitore</p>
          <p className="empty-state-text">
            {searchTerm ? "Nessun risultato trovato" : "Aggiungi il tuo primo fornitore"}
          </p>
        </div>
      )}
    </div>
  );
}
